import React, { useState } from "react";
import { Carousel, Badge, ProgressBar, Alert, Modal, Button, Table, Row, Col } from "react-bootstrap";
import ConnectBox from "./ConnectBox";
import axiosInstance from "../../../../utils/axiosInstance";
import ContactOptions from "./ContactOptions";
import verifiedBadge from "../../../assets/verified-badge.png";
import requestedPhoto from "../../../assets/request-photo.jpg";
import config from "../../../../config";
import { calculateAge } from "../../../../utils/helpers";
import { formatLastSeen } from "../../../../utils/timeAgo";
import { useNavigate } from "react-router-dom";
import AstroModal from "./AstroModal";
import ActionMenu from "./ActionMenu";
import { blockUser, unblockUser, reportUser } from "../../../../features/user/userApi";
import { toast } from "../../../Common/Toast";

const ProfileCard = ({
  profile,
  handleConnectClick,
  handleConnectionCancelled,
  activeIndex,
  setActiveIndex,
  chatBoxOpen,
  dnaMatches = false,
  user = {},
  onProfileUpdate,
}) => {
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showAstroModal, setShowAstroModal] = useState(false);
  const [showCrossModalReport, setShowCrossModalReport] = useState(false);
  const [crossModalCompatibility, setCrossModalCompatibility] = useState(null);
  const [loadingCompatibility, setLoadingCompatibility] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const navigate = useNavigate();

  const getShortDescription = (text) => {
    const words = text?.split(" ");
    if (!words || words.length <= 40) return text;
    return words.slice(0, 40).join(" ") + "...";
  };

  const handleToggleDescription = (e) => {
    e.preventDefault();
    setShowFullDescription((prev) => !prev);
  };

  const safeRenderText = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (typeof value === "object" && value.text) return value.text;
    return String(value);
  };

  const handleProfileClick = () => {
    navigate(`/profile/${profile.profileId || profile.user_id}`, {
      state: {
        profile,
        currentUser: user,
        source: dnaMatches ? "soulmate-matches" : "regular-matches",
      },
    });
  };

  const calculateSoulmateScore = () => {
    // Prefer the freshly-fetched live compatibility data (from clicking
    // the badge / opening the report) over the stale value that came
    // from the initial matches list fetch. Without this, the badge kept
    // showing the old list-time score even after a fresh one was fetched.
    if (crossModalCompatibility) {
      const compat = crossModalCompatibility.compatibility || crossModalCompatibility;
      if (compat?.soulmateScore !== undefined) return compat.soulmateScore;
    }
    if (profile.soulmate_score !== undefined) return profile.soulmate_score;
    if (profile.soulmateScore !== undefined) return profile.soulmateScore;
    if (profile.crossModalCompatibility?.soulmateScore) return profile.crossModalCompatibility.soulmateScore;
    if (profile.crossModalCompatibility?.compatibility?.soulmateScore) return profile.crossModalCompatibility.compatibility.soulmateScore;
    if (profile.dna_compatibility_score) return profile.dna_compatibility_score;
    if (profile.compatibilityScore) return profile.compatibilityScore;
    return 0;
  };

  const getSoulmateVariant = (score) => {
    if (score === 0) return "secondary";
    if (score >= 85) return "success";
    if (score >= 70) return "info";
    if (score >= 60) return "warning";
    return "danger";
  };

  const getSoulmateLevel = (score) => {
    if (score >= 85) return "Exceptional Soulmate Match";
    if (score >= 70) return "Strong Soulmate Potential";
    if (score >= 60) return "Good Soulmate Potential";
    if (score >= 45) return "Moderate Soulmate Potential";
    return "Limited Soulmate Potential";
  };

  const getScoreVariant = (score) => {
    if (score === 0) return "secondary";
    if (score >= 85) return "success";
    if (score >= 70) return "info";
    if (score >= 60) return "warning";
    return "danger";
  };

  // For Risk: lower is better
  const getRiskVariant = (risk) => {
    if (risk <= 20) return "success";
    if (risk <= 35) return "info";
    if (risk <= 60) return "warning";
    return "danger";
  };

  const fetchCrossModalCompatibility = async () => {
    if (!profile.user_id) return;
    setLoadingCompatibility(true);

    try {
      const response = await axiosInstance.post(`${config.baseURL}/api/dna/cross-modal/calculate`, {
        partner_id: profile.user_id,
      });

      if (response.data.success) {
        setCrossModalCompatibility(response.data);
        setShowCrossModalReport(true);
      } else {
        toast.error("Failed to load compatibility report");
      }
    } catch (error) {
      toast.error("Failed to load compatibility details");
    } finally {
      setLoadingCompatibility(false);
    }
  };

  const getCompatibilityDetails = () => {
    const soulmateScore = calculateSoulmateScore();

    // Prefer the freshly-fetched live compatibility data for the level
    // label and sub-score breakdown too, same reasoning as
    // calculateSoulmateScore above.
    if (crossModalCompatibility) {
      const c = crossModalCompatibility;
      const compat = c.compatibility || c;

      return {
        soulmateScore,
        level: compat?.interpretation?.soulmateLevel || getSoulmateLevel(soulmateScore),
        hasCrossModalData: true,
        totalScore: compat?.totalScore || 0,
        subScores: compat?.subScores || {},
        familyScore: compat?.familyPlanningScore || 0,
        futureFamilyRiskScore: compat?.futureFamilyRiskScore,
        futureFamilyRiskLabel: compat?.futureFamilyRiskLabel,
        insights: compat?.interpretation?.insights || [],
        crossModalBreakdown: compat?.crossModalBreakdown,
      };
    }

    if (profile.crossModalCompatibility) {
      const c = profile.crossModalCompatibility;
      const compat = c.compatibility || c;

      return {
        soulmateScore,
        level: compat?.interpretation?.soulmateLevel || getSoulmateLevel(soulmateScore),
        hasCrossModalData: true,
        totalScore: compat?.totalScore || 0,
        subScores: compat?.subScores || {},
        familyScore: compat?.familyPlanningScore || 0,
        futureFamilyRiskScore: compat?.futureFamilyRiskScore,
        futureFamilyRiskLabel: compat?.futureFamilyRiskLabel,
        insights: compat?.interpretation?.insights || [],
        crossModalBreakdown: compat?.crossModalBreakdown,
      };
    }

    return {
      soulmateScore,
      level: getSoulmateLevel(soulmateScore),
      hasCrossModalData: false,
    };
  };

  const renderSoulmateBadge = () => {
    if (!dnaMatches) return null;

    const soulmateScore = calculateSoulmateScore();

    if (soulmateScore === 0) {
      return (
        <Badge bg="secondary" className="ms-2 soulmate-badge" style={{ fontSize: "0.75rem", padding: "0.4rem 0.6rem" }}>
          💖 Soulmate Analysis
        </Badge>
      );
    }

    return (
      <Badge
        bg={getSoulmateVariant(soulmateScore)}
        className="ms-2 soulmate-badge"
        style={{
          fontSize: "0.75rem",
          padding: "0.4rem 0.6rem",
          cursor: "pointer",
          border: "2px solid white",
          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
          transition: "all 0.3s ease",
        }}
        onClick={fetchCrossModalCompatibility}
        title={`Soulmate Match: ${soulmateScore}/100`}
      >
        💖 Soulmate {soulmateScore}/100
      </Badge>
    );
  };

  const renderSubScoreBadges = () => {
    const compatibility = getCompatibilityDetails();
    if (!compatibility.hasCrossModalData || !compatibility.subScores) return null;

    const topSubScores = Object.entries(compatibility.subScores)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3);

    return (
      <div className="d-flex flex-wrap gap-1 mt-1">
        {topSubScores.map(([key, score]) => (
          <Badge
            key={key}
            bg="light"
            text="dark"
            className="small border"
            style={{ fontSize: "0.65rem" }}
            title={`${key.replace(/_/g, " ")}: ${Math.round(score)}%`}
          >
            {getModalityIcon(key)} {Math.round(score)}%
          </Badge>
        ))}
        {Object.keys(compatibility.subScores).length > 3 && (
          <Badge
            bg="light"
            text="dark"
            className="small border"
            style={{ fontSize: "0.65rem", cursor: "pointer" }}
            onClick={fetchCrossModalCompatibility}
            title="Click for full report"
          >
            +{Object.keys(compatibility.subScores).length - 3} more
          </Badge>
        )}
      </div>
    );
  };

  const renderCrossModalReport = () => {
    if (!crossModalCompatibility) return null;

    const compatibility = crossModalCompatibility.compatibility || crossModalCompatibility;
    const users = crossModalCompatibility.users;

    const soulmateScore = compatibility.soulmateScore ?? compatibility.soulmateMatchScore ?? 0;
    const totalScore = compatibility.totalScore ?? soulmateScore;

    const familyPlanningScore = compatibility.familyPlanningScore ?? 0;
    const futureFamilyRiskScore = compatibility.futureFamilyRiskScore ?? Math.max(0, 100 - familyPlanningScore);
    const futureFamilyRiskLabel = compatibility.futureFamilyRiskLabel ?? "Risk";

    const breakdown = compatibility.crossModalBreakdown;

    return (
      <Modal show={showCrossModalReport} onHide={() => setShowCrossModalReport(false)} size="xl" centered scrollable>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>💖 HelixFoundary Two-Gauge Compatibility Report</Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {/* Header with users */}
          <div className="row mb-4">
            <div className="col-6 text-center">
              <div className="p-3 border rounded text-white" style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
                <h6>Your Profile</h6>
                <strong>{safeRenderText(users?.user?.name) || "You"}</strong>
                <br />
                <small className="opacity-75">
                  {users?.user?.genetic_profile?.has_data ? `${users.user.genetic_profile.conditions_count} genetic markers` : "No genetic data"}
                </small>
              </div>
            </div>

            <div className="col-6 text-center">
              <div className="p-3 border rounded text-white" style={{ background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)" }}>
                <h6>Partner Profile</h6>
                <strong>{safeRenderText(users?.partner?.name) || `${profile.first_name} ${profile.last_name}`}</strong>
                <br />
                <small className="opacity-75">
                  {users?.partner?.genetic_profile?.has_data ? `${users.partner.genetic_profile.conditions_count} genetic markers` : "No genetic data"}
                </small>
              </div>
            </div>
          </div>

          {/* Gauge #1: Soulmate Match */}
          <div
            className="text-center mb-4 p-4 border rounded"
            style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white" }}
          >
            <h4 className="mb-2">💖 Soulmate Match</h4>
            <h1 className="display-3 fw-bold mb-2">{Math.round(soulmateScore)}/100</h1>
            <h5 className="mb-3">{safeRenderText(compatibility?.interpretation?.soulmateLevel) || getSoulmateLevel(soulmateScore)}</h5>
            <ProgressBar now={soulmateScore} style={{ height: "18px", backgroundColor: "rgba(255,255,255,0.2)" }} className="mb-2" />
            <p className="mb-0">{safeRenderText(compatibility?.interpretation?.recommendation) || "Soulmate-first weighting: Psy + Profile dominate early decisions."}</p>
          </div>

          {/* Gauge #2: Future Family Risk */}
          <div className="text-center mb-4 p-4 border rounded bg-light">
            <h4 className="mb-2">👶 Future Family Risk</h4>
            <h1 className="display-4 fw-bold mb-1">{Math.round(futureFamilyRiskScore)}/100</h1>
            <h6 className={`mb-3 text-${getRiskVariant(futureFamilyRiskScore)}`}>{futureFamilyRiskLabel} (Lower is better)</h6>

            {/* risk bar shows risk directly */}
            <ProgressBar variant={getRiskVariant(futureFamilyRiskScore)} now={futureFamilyRiskScore} style={{ height: "14px" }} className="mb-2" />
            <small className="text-muted">
              This gauge becomes most relevant when you’re serious and discussing kids. It’s informational and not medical advice.
            </small>
          </div>

          {/* Weighted breakdown table (Soulmate + Family) */}
          <div className="mb-4">
            <h5 className="mb-3">📊 Cross-Modal Weighted Breakdown</h5>

            <div className="table-responsive">
              <Table striped bordered hover size="sm">
                <thead className="table-dark">
                  <tr>
                    <th>Modality</th>
                    <th>Score</th>
                    <th>Weight</th>
                    <th>Contribution</th>
                  </tr>
                </thead>

                <tbody>
                  {breakdown?.contributions &&
                    Object.entries(breakdown.contributions).map(([modality, data]) => (
                      <tr key={modality}>
                        <td className="fw-medium text-capitalize">
                          {getModalityIcon(modality)} {modality.replace(/_/g, " ")}
                        </td>

                        <td>
                          <Badge bg={getScoreVariant(data.score)} className="me-2">
                            {Math.round(data.score)}%
                          </Badge>
                        </td>

                        <td>
                          <Badge bg="secondary">{Math.round(data.weight * 100)}%</Badge>
                        </td>

                        <td>
                          <strong>{Number(data.contribution).toFixed(1)}</strong>
                        </td>
                      </tr>
                    ))}

                  <tr className="table-success">
                    <td className="fw-bold">💖 Total Compatibility</td>
                    <td colSpan={3}>
                      <Badge bg="success" className="fs-6">
                        {Math.round(totalScore)}/100
                      </Badge>
                    </td>
                  </tr>

                  <tr className="table-info">
                    <td className="fw-bold">👶 Family Planning Score</td>
                    <td colSpan={3}>
                      <Badge bg="info" className="fs-6">
                        {Math.round(familyPlanningScore)}/100
                      </Badge>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </div>

          {/* Insights */}
          {compatibility?.interpretation?.insights?.length > 0 && (
            <div className="mb-4">
              <h5 className="mb-3">💡 Key Insights</h5>
              {compatibility.interpretation.insights.map((insight, idx) => (
                <Alert
                  key={idx}
                  variant={insight.level === "critical" ? "danger" : insight.level === "warning" ? "warning" : "info"}
                >
                  <div className="fw-bold">{safeRenderText(insight.message)}</div>
                  <div className="small">{safeRenderText(insight.details)}</div>
                </Alert>
              ))}
            </div>
          )}

          {/* Disclaimer */}
          <div className="p-3 border rounded bg-light mt-3">
            <small className="text-muted">
              <strong>Disclaimer:</strong> This report is informational. Soulmate Match emphasizes emotional + lifestyle compatibility.
              Future Family Risk is intended for later-stage planning and does not replace professional medical advice. Consult qualified
              healthcare professionals and genetic counselors for medical decisions. © {new Date().getFullYear()} HelixFoundary.
            </small>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCrossModalReport(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  const getModalityIcon = (modality) => {
    const icons = {
      main_compatibility_score: "🧬",
      hla_compatibility_score: "🛡️",
      psychogenetic_score: "🧠",
      reproductive_score: "👶",
      profile_affinity_score: "💞",
    };
    return icons[modality] || "📊";
  };

  // Handle block/unblock actions
  const handleBlock = async (blockedUserId) => {
    try {
      await blockUser(user.user_id, blockedUserId);
      setIsBlocked(true);
      onProfileUpdate && onProfileUpdate();
      toast.success("User blocked successfully");
    } catch (error) {
      toast.error("Failed to block user");
    }
  };

  const handleUnblock = async (blockedUserId) => {
    try {
      await unblockUser(user.user_id, blockedUserId);
      setIsBlocked(false);
      onProfileUpdate && onProfileUpdate();
      toast.success("User unblocked successfully");
    } catch (error) {
      toast.error("Failed to unblock user");
    }
  };

  const handleReport = async (reportedUserId, reason) => {
    try {
      await reportUser(user.user_id, reportedUserId, reason);
      toast.success("User reported successfully");
    } catch (error) {
      toast.error("Failed to report user");
    }
  };

  // Parse gallery images and include profile image as first item
  const profile_gallery_images = profile?.profile_gallery_images ? JSON.parse(profile.profile_gallery_images) : [];
  let allImages = [];
  if (profile_gallery_images.length > 0) {
    allImages = [{ id: "profile-main", filename: profile.profile_image }, ...profile_gallery_images];
  }

  const compatibility = getCompatibilityDetails();

  return (
    <>
      <div className="profile-part mb-4">
        <div className="row g-0">
          {/* Profile Image Carousel */}
          <div className="col-md-4 position-relative cursor-pointer" onClick={handleProfileClick}>
            {allImages.length > 0 ? (
              <>
                <Carousel activeIndex={activeIndex} onSelect={setActiveIndex} interval={null} indicators={allImages.length > 1} controls={allImages.length > 1}>
                  {allImages.map((img, idx) => (
                    <Carousel.Item key={img.id || idx}>
                      <img
                        src={`${config.baseURL}/uploads/profiles/${img.filename}`}
                        className="d-block w-100"
                        alt={`Profile ${idx + 1}`}
                        style={{ height: "330px", objectFit: "cover", borderRadius: "8px 0 0 8px" }}
                      />
                    </Carousel.Item>
                  ))}
                </Carousel>
              </>
            ) : (
              <div className="position-relative h-100">
                <img
                  src={profile.profile_image ? `${config.baseURL}/uploads/profiles/${profile.profile_image}` : "/images/no-image-found.webp"}
                  alt="Profile"
                  style={{ height: "330px", objectFit: "cover", borderRadius: "8px 0 0 8px" }}
                />
                {profile.image === requestedPhoto && (
                  <div className="request-photonew position-absolute bottom-0 start-0 w-100 text-center py-2" style={{ backgroundColor: "#b10f62", color: "white", fontWeight: "600" }}>
                    Request Photo
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Profile Information */}
          <div className="col-md-6">
            <div className="p-3 h-100 d-flex flex-column">
              <div className="mb-2">
                <h5 className="mb-1 d-flex justify-content-between align-items-center">
                  <span className="profile-name-link" onClick={handleProfileClick} style={{ cursor: "pointer", color: "inherit", fontWeight: "600" }}>
                    {profile.first_name} {profile.last_name}
                    {profile.isVerified && <img src={verifiedBadge} alt="Verified" className="ms-2" style={{ width: "20px", height: "20px" }} />}
                  </span>

                  <div className="d-flex align-items-center">
                    {renderSoulmateBadge()}
                    <ActionMenu profile={profile} user={user} onBlock={handleBlock} onUnblock={handleUnblock} onReport={handleReport} isBlocked={isBlocked} />
                  </div>
                </h5>

                {dnaMatches && compatibility.soulmateScore > 0 && (
                  <div className="mb-1">
                    <small className={`text-${getSoulmateVariant(compatibility.soulmateScore)} fw-bold`}>
                      💖 {compatibility.level}
                      {compatibility.futureFamilyRiskScore !== undefined && (
                        <span className="ms-2 text-muted">
                          👶 Risk: <strong className={`text-${getRiskVariant(compatibility.futureFamilyRiskScore)}`}>{Math.round(compatibility.futureFamilyRiskScore)}</strong>/100
                        </span>
                      )}
                    </small>
                  </div>
                )}

                {renderSubScoreBadges()}
              </div>

              <div className="d-flex gap-3 mb-2 small">
                {profile.online ? (
                  <span className="text-success">
                    <i className="fa fa-circle me-1" aria-hidden="true"></i> Online now
                  </span>
                ) : (
                  <span className="text-muted">
                    <i className="fa fa-clock-o me-1" aria-hidden="true"></i> {`${formatLastSeen(profile.online_status)}`}
                  </span>
                )}
                <span className="text-muted">
                  <i className="fa fa-users me-1" aria-hidden="true"></i> {profile.looking_for === "Bride" ? " Looking for Bride" : " Looking for Groom"}
                </span>
                <span className="text-warning cursor-pointer" onClick={() => setShowAstroModal(true)} style={{ cursor: "pointer" }}>
                  <i className="fa fa-globe me-1" aria-hidden="true"></i> Astro
                </span>
              </div>

              <hr className="my-2" />

              <div className="profile-detail flex-grow-1">
                <div className="row text-dark mb-2">
                  <div className="col-6">
                    <div className="mb-1">
                      <i className="fa fa-birthday-cake me-1 text-primary"></i>
                      {calculateAge(profile.birth_year, profile.birth_month, profile.birth_day)} yrs, {profile.height}
                    </div>
                    <div className="mb-1">
                      <i className="fa fa-briefcase me-1 text-primary"></i>
                      {profile.profession}
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="mb-1">
                      <i className="fa fa-map-marker me-1 text-primary"></i>
                      {profile.city}, {profile.country}
                    </div>
                  </div>
                </div>

                {loadingCompatibility && (
                  <div className="text-center py-2">
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Loading compatibility...</span>
                    </div>
                    <p className="small text-muted mt-1">Analyzing soulmate compatibility...</p>
                  </div>
                )}

                {!dnaMatches && profile.profile_description && (
                  <div className="mt-2">
                    <p className="mb-0 text-dark fs-6">
                      {showFullDescription || profile.profile_description?.split(" ").length <= 40
                        ? profile.profile_description
                        : getShortDescription(profile.profile_description)}
                      {profile.profile_description?.split(" ").length > 40 && (
                        <a href="#" className="text-primary ms-1" onClick={handleToggleDescription} style={{ textDecoration: "none", fontWeight: "500" }}>
                          {showFullDescription ? "Show less" : "Read more"}
                        </a>
                      )}
                    </p>
                  </div>
                )}

                {dnaMatches && compatibility.hasCrossModalData && (
                  <div className="mt-2">
                    <small className="text-primary cursor-pointer mt-1 d-block" onClick={fetchCrossModalCompatibility} style={{ cursor: "pointer", fontSize: "0.75rem" }}>
                      <i className="fa fa-chart-line me-1"></i>
                      View two-gauge report (Soulmate Match + Future Family Risk)
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Connect Button Section */}
          <div className="col-md-2 d-flex align-items-center justify-content-center connect-now p-2">
            {profile.connectionStatus === 'accepted' ? (
              <ContactOptions
                profile={profile}
                chatBoxOpen={chatBoxOpen}
              />
            ) : (
              <ConnectBox
                profile={profile}
                id={profile.user_id}
                profileId={profile.profileId}
                onConnectionSent={handleConnectClick}
                onConnectionCancelled={handleConnectionCancelled}
              />
            )}
          </div>
        </div>
      </div>

      <AstroModal show={showAstroModal} onHide={() => setShowAstroModal(false)} profile={profile} currentUser={user} handleConnectClick={handleConnectClick} chatBoxOpen={chatBoxOpen} />
      {renderCrossModalReport()}
    </>
  );
};

export default ProfileCard;